import React, { Fragment, useEffect, useState } from "react";
import CssBaseline from "@material-ui/core/CssBaseline";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import Button from "@material-ui/core/Button";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { Typography } from "@material-ui/core";
import { BottomNavBar, DoctorComponent } from "../common";
import { fontSize } from "../../styles";
import {
  checkGameStatus,
  evaluateResult,
  navigateGameScreen,
} from "../../service/logic";
import { setGameStatus } from "../../redux/dispatchers";
import { GAME_STAGE } from "../../utils/constant";
import { history } from "../../index";
import { useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";

const useStyles = makeStyles((theme) => ({
  canvas: {
    paddingTop: "5%",
  },
  card: {
    [theme.breakpoints.up("lg")]: {
      width: 650,
      height: 460,
      padding: theme.spacing(2, 3, 3, 3),
    },
    [theme.breakpoints.down("md")]: {
      width: 450,
      height: 400,
      padding: theme.spacing(1, 2, 0, 3),
    },
    [theme.breakpoints.down("sm")]: {
      width: 300,
      height: 450,
      padding: theme.spacing(2, 2, 0, 2),
    },
    [theme.breakpoints.down("xs")]: {
      width: 250,
      padding: theme.spacing(1, 1, 0, 1),
      marginLeft: "5%",
    },
    border: "1px solid black",
    backgroundColor: "white",
    paddingBottom: theme.spacing(0.2),
    marginLeft: "10%",
    marginBottom: 75,
  },
  mark: {
    textAlign: "center",
  },
  shortText: {
    alignItems: "flex-end",
    display: "flex",
    fontSize: fontSize.h3,
    [theme.breakpoints.down("md")]: {
      fontSize: fontSize.h4,
    },
  },
  table: {
    display: "flex",
    flex: 4,
    flexDirection: "column",
  },
  row: {
    padding: 0,
    display: "flex",
    justifyContents: "space-between",
    flexDirection: "row",
    marginBottom: 10,
  },
  contentRow: {
    display: "flex",
    justifyContents: "space-between",
    flexDirection: "row",
    height: 45,
    padding: 0,
    marginBottom: 5,
    [theme.breakpoints.down("sm")]: {
      height: 30,
    },
  },
  cell: {
    padding: 0,
    marginRight: 5,
    marginLeft: 5,
    display: "flex",
    flex: 1,
    color: "white",
    borderRadius: 10,
    justifyContent: "center",
    [theme.breakpoints.down("sm")]: {
      marginRight: 3,
      marginLeft: 3,
      borderRadius: 0,
    },
  },
  contentCell: {
    padding: 0,
    marginRight: 5,
    marginLeft: 5,
    display: "flex",
    flex: 1,
    borderStyle: "solid",
    borderWidth: "1px",
    borderRadius: 10,
    justifyContent: "center",
    [theme.breakpoints.down("sm")]: {
      marginRight: 3,
      marginLeft: 3,
    },
  },
  typeCell: {
    padding: 0,
    marginRight: 5,
    marginLeft: 5,
    display: "flex",
    flex: 1,
    justifyContent: "flex-start",
    fontSize: fontSize.h4,
    [theme.breakpoints.down("md")]: {
      fontSize: 13,
    },
    [theme.breakpoints.down("sm")]: {
      marginBottom: 3,
      marginRight: 3,
      marginLeft: 3,
    },
  },
  text: {
    [theme.breakpoints.down("sm")]: {
      fontSize: 14,
    },
  },
  button: {
    textTransform: "none",
    marginTop: 20,
    padding: "10px 40px",
    backgroundColor: "#1D3557",
    borderRadius: 10,
    color: "white",
    [theme.breakpoints.down("md")]: {
      marginTop: 10,
      padding: "5px 30px",
    },
  },
}));

function FinalMarks() {
  const classes = useStyles();
  const theme = useTheme();
  const isWidthSm = useMediaQuery(theme.breakpoints.down("sm"));
  const [marks, setMarks] = useState([]);
  const [mark, setMark] = useState(0);
  const [pct, setPct] = useState(0);
  const [total, setTotal] = useState(0);

  // get case deductedScores
  const { deductedScores } = useSelector((state) => state.gameStates.case);

  useEffect(() => {
    // check if user is allowed to enter page
    if (!checkGameStatus(GAME_STAGE.FINAL_MARKS)) {
      // if not allowed, navigate to case list page
      history.push("/cases");
    }
    evaluateResult().then((data) => {
      const types = [
        "History Taking",
        "Examination",
        "Investigation",
        "Diagnosis",
        "Management",
      ];

      const tmp = [
        "history",
        "examination",
        "investigation",
        "diagnosis",
        "management",
      ].map((t, index) => ({
        type: types[index],
        correct: data[t]["selectedAndCorrect"].reduce(
          (total, cur) => total + +cur.scores,
          0
        ),
        missed: data[t]["missed"].length * deductedScores,
        irrelevant: data[t]["selectedAndIncorrect"].length * deductedScores,
      }));
      // set marks
      setMarks(tmp);
      setPct(Math.round((data.percentageScore + Number.EPSILON) * 100) / 100);
      setTotal(data.totalScores);
      setMark(data.scores);
    });
  }, [deductedScores]);

  return (
    <React.Fragment>
      <CssBaseline />
      <DoctorComponent smile />
      <div className={classes.canvas}>
        <div className={`${classes.card}`}>
          {/*title*/}
          <h3 className={classes.mark}>
            Final Scores: {mark} / {total} ({pct} %)
          </h3>
          {/*table*/}
          <div className={classes.table}>
            {/*header*/}
            <List className={classes.row}>
              {isWidthSm ? null : <ListItem className={classes.cell} />}

              <ListItem
                className={classes.cell}
                style={{ backgroundColor: "#4CC87D" }}
              >
                <Typography className={classes.text}>Correct</Typography>
              </ListItem>
              <ListItem
                className={classes.cell}
                style={{ backgroundColor: "#FF0000" }}
              >
                <Typography className={classes.text}>Missed</Typography>
              </ListItem>
              <ListItem
                className={classes.cell}
                style={{ backgroundColor: "#457B9D" }}
              >
                <Typography className={classes.text}>Irrelevant</Typography>
              </ListItem>
            </List>
            {/*content*/}
            {marks.map((mark) => (
              <Fragment key={uuidv4()}>
                {!isWidthSm ? null : (
                  <ListItem className={classes.typeCell}>{mark.type}</ListItem> //put type on top
                )}

                <List className={classes.contentRow}>
                  {isWidthSm ? null : (
                    <ListItem className={classes.typeCell}>
                      {mark.type}
                    </ListItem> //put type in the same row
                  )}
                  <ListItem
                    className={classes.contentCell}
                    style={{
                      borderColor: "#4CC87D",
                      borderWidth: "1px",
                    }}
                  >
                    <Typography className={classes.text}>
                      + {mark.correct}
                    </Typography>
                  </ListItem>
                  <ListItem
                    className={classes.contentCell}
                    style={{
                      borderColor: "#FF0000",
                      borderWidth: "1px",
                    }}
                  >
                    <Typography className={classes.text}>
                      - {mark.missed}
                    </Typography>
                  </ListItem>
                  <ListItem
                    className={classes.contentCell}
                    style={{
                      borderColor: "#457B9D",
                      borderWidth: "1px",
                    }}
                  >
                    <Typography className={classes.text}>
                      - {mark.irrelevant}
                    </Typography>
                  </ListItem>
                </List>
              </Fragment>
            ))}
            <div>
              <Button
                variant="contained"
                color="primary"
                className={classes.button}
                onClick={() => {
                  setGameStatus(15);
                  navigateGameScreen(0);
                }}
              >
                Full Case Summary
              </Button>
            </div>
          </div>
        </div>
        <BottomNavBar />
      </div>
    </React.Fragment>
  );
}

export default FinalMarks;
